import { Inject, Injectable } from '@angular/core'
import type {
  FloApiSearchResponseData,
  SearchContentType,
  SearchMeta,
  SearchQueryParams
} from './search-data.service.interface'
import { HttpClient, HttpParams } from '@angular/common/http'
import { catchError, switchMap } from 'rxjs/operators'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import type { FloResponse } from '@flocasts/flosports30-types/dist/flo-rest-api/response'
import { Observable, of } from 'rxjs'
import {
  EXPERIENCE_API_LEGACY_CORE_SEARCH_PATH,
  EXPERIENCE_API_SEARCH_BASE_PATH,
  EXPERIENCE_API_VERSION
} from '../app.config'
import { FilterablePartial, WebContentWithRightRailLayout } from '@flocasts/experience-service-types'
import { ParamMap } from '@angular/router'
import { getSearchHttpParams } from '../search/search.utility'
import { VerticalService } from './vertical.service'

export const emptyMeta: SearchMeta = {
  total: 0,
  page: 1,
  limit: 1,
  offset: 0,
  pages: 1,
  extra: [
    {
      facets: {}
    },
    {
      pre_faceted_types: [],
      pre_faceted_display_names: {}
    }
  ]
}
@Injectable({
  providedIn: 'root'
})
export class SearchDataService {
  constructor(
    private readonly http: HttpClient,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly verticalService: VerticalService
  ) {}

  public searchCategories(
    searchQueryParams: SearchQueryParams
  ): Observable<FloResponse<ReadonlyArray<FloApiSearchResponseData>, SearchMeta>> {
    const params = new HttpParams({
      fromObject: {
        version: EXPERIENCE_API_VERSION,
        user_preferences_only: 1,
        ...searchQueryParams
      }
    })

    return this.http
      .get<FloResponse<ReadonlyArray<FloApiSearchResponseData>, SearchMeta>>(EXPERIENCE_API_LEGACY_CORE_SEARCH_PATH, {
        params
      })
      .pipe(
        catchError((err: unknown) => {
          this.logger.error('Search Data Service: Invalid API Response.', { err, searchQueryParams })
          return of({ meta: emptyMeta, data: [] })
        })
      )
  }

  public searchAll(
    query?: string,
    page?: string,
    type?: SearchContentType,
    useOFP?: boolean
  ): Observable<FloResponse<ReadonlyArray<FloApiSearchResponseData>, SearchMeta>> {
    // page and limit are the only pagination params supported
    let params = new HttpParams({
      fromObject: {
        version: EXPERIENCE_API_VERSION,
        limit: '25',
        view: 'global-search-web', // optimized cloudsearch sorting
        fields: 'data<1>' // narrow response size down
      }
    })
    params = query ? params.set('q', query) : params
    params = page ? params.set('page', page) : params
    params = type ? params.set('type', type) : params
    params = useOFP ? params.set('useOfp', 'true') : params
    return this.http
      .get<FloResponse<ReadonlyArray<FloApiSearchResponseData>, SearchMeta>>(EXPERIENCE_API_LEGACY_CORE_SEARCH_PATH, {
        params
      })
      .pipe(
        catchError((err: unknown) => {
          this.logger.error('Search Data Service: Invalid API Response.', { err, query })
          return of({ meta: emptyMeta, data: [] })
        })
      )
  }

  public getSearchLayout(
    queryParams: ParamMap | null,
    useOfp?: boolean
  ): Observable<WebContentWithRightRailLayout | undefined> {
    return this.verticalService.siteId$.pipe(
      switchMap(siteId => {
        const params = getSearchHttpParams(queryParams, siteId, useOfp)
        return this.http.get<WebContentWithRightRailLayout>(EXPERIENCE_API_SEARCH_BASE_PATH, { params }).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }

  public getSearchFilterablePartial(
    queryParams: ParamMap | null,
    useOfp?: boolean
  ): Observable<FilterablePartial | undefined> {
    return this.verticalService.siteId$.pipe(
      switchMap(siteId => {
        const params = getSearchHttpParams(queryParams, siteId, useOfp)
        return this.http.get<FilterablePartial>(`${EXPERIENCE_API_SEARCH_BASE_PATH}/partial`, { params }).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }
}
